// src/routes/guide/index.tsx
//
// In-app User Guide.
//
// A newcomer-friendly walkthrough of Asset Risk Manager. A short quick-start
// gets a new user productive, then a reference section covers each feature
// area in plain English. Content mirrors the real navigation and screens so it
// stays honest; admin-only areas are tagged.
//
// UX details (mirrors the sibling Consensus guide, built natively for ARM):
//   - A sticky "On this page" rail stays visible while scrolling on desktop and
//     highlights the current section (IntersectionObserver scroll-spy). On
//     mobile the same list renders inline above the content.
//   - "Print / Save as PDF" calls window.print(). The app chrome (top-bar,
//     sidebar, module header) and the button itself are hidden when printing
//     via print-only styles (see the `print:hidden` classes here + the
//     `@media print` block in index.css).

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Squares2X2Icon,
  CubeIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ChartBarIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  UsersIcon,
  BuildingOffice2Icon,
  RocketLaunchIcon,
  PrinterIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';
import { ModuleHeader } from '../../components/layout/ModuleHeader';
import { Button } from '../../components/ui/Button';

export const Route = createFileRoute('/guide/')({
  component: UserGuidePage,
});

// ── Contents (drives both the sticky rail and the scroll-spy) ────────────────
const CONTENTS: { href: string; label: string }[] = [
  { href: 'quick-start', label: 'Quick start' },
  { href: 'dashboard', label: 'Dashboard' },
  { href: 'assets', label: 'Assets' },
  { href: 'infosec-risk', label: 'InfoSec Risk' },
  { href: 'business-risk', label: 'Business Risk' },
  { href: 'soa', label: 'Statement of Applicability' },
  { href: 'raci', label: 'RACI' },
  { href: 'reports', label: 'Reports' },
  { href: 'profile', label: 'Your profile & security' },
  { href: 'admin', label: 'Admin' },
  { href: 'users', label: 'Users' },
  { href: 'platform', label: 'Platform' },
];

// Jump-nav shown in the sticky desktop rail and inline on mobile.
function TocNav({ active }: { active: string }) {
  return (
    <nav className="text-sm">
      <p
        className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wider"
        style={{ color: 'var(--color-text-subtle)' }}
      >
        On this page
      </p>
      <div className="space-y-0.5">
        {CONTENTS.map((c) => {
          const isActive = c.href === active;
          return (
            <a
              key={c.href}
              href={`#${c.href}`}
              aria-current={isActive ? 'true' : undefined}
              className="block rounded-lg px-2 py-1 font-medium transition-colors"
              style={{
                color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)',
                background: isActive ? 'var(--color-accent-subtle)' : 'transparent',
              }}
            >
              {c.label}
            </a>
          );
        })}
      </div>
    </nav>
  );
}

// Highlight the section nearest the top of the scroll region as you scroll.
function useActiveSection(root: React.RefObject<HTMLElement | null>): string {
  const [active, setActive] = useState(CONTENTS[0].href);
  useEffect(() => {
    const els = CONTENTS
      .map((c) => document.getElementById(c.href))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      // Trigger just below the sticky module header, within the scroll region.
      { root: root.current, rootMargin: '-80px 0px -65% 0px', threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [root]);
  return active;
}

// A reference card for one feature area.
function Section({ id, icon, title, tag, children }: {
  id: string; icon: ReactNode; title: string; tag?: string; children: ReactNode;
}) {
  return (
    <section
      id={id}
      className="rounded-xl border p-5 scroll-mt-24"
      style={{
        background: 'var(--color-bg)',
        borderColor: 'var(--color-border)',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span style={{ color: 'var(--color-product)' }}>{icon}</span>
        <h2 className="text-lg font-bold m-0" style={{ color: 'var(--color-text)' }}>
          {title}
        </h2>
        {tag && (
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{
              background: 'var(--color-accent-subtle)',
              color: 'var(--color-accent)',
            }}
          >
            {tag}
          </span>
        )}
      </div>
      <div
        className="text-sm space-y-2 leading-relaxed"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {children}
      </div>
    </section>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span
        className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center"
        style={{ background: 'var(--color-accent-subtle)', color: 'var(--color-accent)' }}
      >
        {n}
      </span>
      <div>
        <p className="font-semibold m-0" style={{ color: 'var(--color-text)' }}>{title}</p>
        <p className="text-sm mt-0.5 m-0" style={{ color: 'var(--color-text-muted)' }}>{children}</p>
      </div>
    </li>
  );
}

function UserGuidePage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const active = useActiveSection(scrollRef);

  const iconCls = 'w-5 h-5';

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      {/* Sidebar hidden when printing so the printout is just the guide. */}
      <NavigationSidebar className="print:hidden" />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <ModuleHeader
          title="User guide"
          subtitle="How to get the most out of Asset Risk Manager"
        >
          <Button
            variant="secondary"
            size="sm"
            icon="Printer"
            onClick={() => window.print()}
            className="print:hidden"
          >
            Print / Save as PDF
          </Button>
        </ModuleHeader>

        {/* Scroll region — the IntersectionObserver root + sticky rail context. */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto print:overflow-visible">
          <div className="mx-auto max-w-5xl px-6 py-6 flex items-start gap-8">
            {/* Sticky contents rail (desktop only). */}
            <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-4 self-start print:hidden">
              <TocNav active={active} />
            </aside>

            {/* Guide body */}
            <div className="min-w-0 flex-1 max-w-3xl space-y-4">
              {/* Intro */}
              <div
                className="rounded-xl border p-5 flex items-start gap-3"
                style={{
                  background: 'var(--color-bg)',
                  borderColor: 'var(--color-border)',
                  boxShadow: 'var(--shadow-1)',
                }}
              >
                <BookOpenIcon
                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                  style={{ color: 'var(--color-product)' }}
                />
                <p className="text-sm m-0" style={{ color: 'var(--color-text-muted)' }}>
                  Asset Risk Manager helps you build an asset inventory, assess information
                  security and business risks, and produce an audit-ready Statement of
                  Applicability for ISO 27001. New here? Start with the quick start below — the
                  rest is a reference you can dip into any time.
                </p>
              </div>

              {/* Contents — inline on mobile (the sticky rail is hidden below lg). */}
              <div
                className="lg:hidden rounded-xl border p-4"
                style={{
                  background: 'var(--color-bg)',
                  borderColor: 'var(--color-border)',
                  boxShadow: 'var(--shadow-1)',
                }}
              >
                <TocNav active={active} />
              </div>

              {/* Quick start */}
              <Section
                id="quick-start"
                icon={<RocketLaunchIcon className={iconCls} />}
                title="Quick start"
              >
                <ol className="space-y-3 list-none pl-0 m-0">
                  <Step n={1} title="Sign in and secure your account">
                    Sign in with your email and password. Open <strong>your profile</strong> from
                    the user menu (top-right) and set up two-factor authentication — a passkey,
                    an authenticator app, or SMS — then save your recovery codes somewhere safe.
                  </Step>
                  <Step n={2} title="Build your asset inventory">
                    Go to <strong>Assets</strong> and add the equipment, systems and information
                    assets you want to track. Flag the ones in scope for ISO 27001, and record
                    audit, calibration, certification and PAT dates so the app can warn you before
                    they expire.
                  </Step>
                  <Step n={3} title="Assess your risks">
                    Use <strong>InfoSec Risk</strong> to assess ISO 27001 risks against your
                    assets, and <strong>Business Risk</strong> for wider operational risks. Each
                    risk gets an uncontrolled and a managed rating from the 5×5 risk matrix.
                  </Step>
                  <Step n={4} title="Complete your Statement of Applicability">
                    In <strong>SoA</strong>, mark each of the 93 ISO 27001 controls as applicable
                    or not and record your justification. This is the core audit document.
                  </Step>
                  <Step n={5} title="Report and export for audit">
                    Use <strong>Reports</strong> to review coverage and build custom exports, and
                    export the SoA and RACI as audit-ready PDFs when you need to share evidence.
                  </Step>
                </ol>
              </Section>

              {/* Reference header */}
              <div className="pt-2">
                <h2
                  className="text-xs font-semibold uppercase tracking-wider px-1 m-0"
                  style={{ color: 'var(--color-text-subtle)' }}
                >
                  Reference
                </h2>
              </div>

              <Section id="dashboard" icon={<Squares2X2Icon className={iconCls} />} title="Dashboard">
                <p className="m-0">
                  Your home screen. It gives an at-a-glance overview of your workspace — asset
                  counts, how your information security and business risks break down by level,
                  Statement of Applicability progress, and anything with audits or certifications
                  coming up for renewal.
                </p>
              </Section>

              <Section id="assets" icon={<CubeIcon className={iconCls} />} title="Assets">
                <p className="m-0">The register of everything you're tracking. For each asset you can record:</p>
                <ul className="list-disc pl-5 space-y-1 m-0">
                  <li><strong>Details</strong> — make, model, asset group, SKU, quantity, location and owner.</li>
                  <li><strong>ISO 27001 scope</strong> — flag whether an asset is in scope for information security, and pick its category.</li>
                  <li><strong>Lifecycle dates</strong> — last audit, calibration, certification and PAT test, each with a period, so ARM can flag what's due or expired.</li>
                  <li><strong>Labels</strong> — generate QR codes and barcodes to print for physical assets.</li>
                  <li><strong>Bulk import/export</strong> — bring assets in from a CSV, or export the list.</li>
                </ul>
              </Section>

              <Section id="infosec-risk" icon={<ExclamationTriangleIcon className={iconCls} />} title="InfoSec Risk">
                <p className="m-0">
                  Information security risk assessments for ISO 27001. Pick an asset category and
                  asset, choose the relevant threat and vulnerability, and rate the risk. Each risk
                  carries an <strong>uncontrolled</strong> rating (before treatment) and a
                  <strong> managed</strong> rating (after your controls), scored on the 5×5
                  likelihood × impact matrix so you can see where treatment is making a difference.
                </p>
              </Section>

              <Section id="business-risk" icon={<ClipboardDocumentListIcon className={iconCls} />} title="Business Risk">
                <p className="m-0">
                  Wider operational and business risks that sit outside information security. It
                  works the same way as InfoSec Risk — an uncontrolled and a managed severity rating
                  on the risk matrix — so all your risks are captured and rated consistently in one
                  place.
                </p>
              </Section>

              <Section id="soa" icon={<ShieldCheckIcon className={iconCls} />} title="Statement of Applicability">
                <p className="m-0">
                  Your Statement of Applicability (SoA) — the central ISO 27001 document. It lists
                  all 93 Annex A controls across the Organisational, People, Physical and
                  Technological themes. For each control you mark whether it's applicable and record
                  a justification. When you're ready, export the whole thing as an audit-ready PDF.
                </p>
              </Section>

              <Section id="raci" icon={<UserGroupIcon className={iconCls} />} title="RACI">
                <p className="m-0">
                  A responsibility matrix that records who is <strong>Responsible</strong>,
                  <strong> Accountable</strong>, <strong>Consulted</strong> and
                  <strong> Informed</strong> for each item. You can maintain a RACI for your SoA
                  controls and for your risk treatments, and export it as a PDF for auditors.
                </p>
              </Section>

              <Section id="reports" icon={<ChartBarIcon className={iconCls} />} title="Reports">
                <p className="m-0">
                  Reporting and analytics across your data. Build custom asset reports by choosing
                  exactly which fields to include, review information security and business risk
                  analytics, and export to CSV or PDF for sharing and record-keeping.
                </p>
              </Section>

              <Section id="profile" icon={<UserCircleIcon className={iconCls} />} title="Your profile & security">
                <p className="m-0">
                  Reached from the user menu in the top-right. Here you manage your own account and
                  sign-in security:
                </p>
                <ul className="list-disc pl-5 space-y-1 m-0">
                  <li><strong>Passkey</strong> — sign in with Face ID, Touch ID, Windows Hello or a security key.</li>
                  <li><strong>Authenticator app</strong> — a 6-digit code from an app such as Google Authenticator.</li>
                  <li><strong>SMS</strong> — a code texted to your phone.</li>
                  <li><strong>Recovery codes</strong> — one-time codes to get back in if you lose your device. Keep them safe.</li>
                  <li><strong>Contact details</strong> — keep your phone number up to date, as it's used for sign-in codes.</li>
                </ul>
                <p className="m-0">
                  If your account belongs to more than one company, the user menu also lets you
                  switch between them. The app signs you out automatically after a period of
                  inactivity (with a warning first) — the length is set by your company admin.
                </p>
              </Section>

              <Section id="admin" icon={<Cog6ToothIcon className={iconCls} />} title="Admin" tag="Admins">
                <p className="m-0">
                  Where company admins configure the workspace for everyone. This includes your
                  locations and asset owners, the rating scales and risk matrix, the date format,
                  the idle session-timeout length, and other per-company settings that shape how the
                  app behaves for your team.
                </p>
              </Section>

              <Section id="users" icon={<UsersIcon className={iconCls} />} title="Users" tag="Admins">
                <p className="m-0">
                  Company admins manage who can sign in from here: invite a new person by email,
                  change someone's role or permission, correct a mistyped email, send a password
                  reset, reset or unlock a locked-out account, and deactivate people who've left.
                </p>
                <p className="m-0">
                  Roles are <strong>Company admin</strong> (manages users and configuration) and
                  <strong> Company user</strong> (works with the data), and each user has either
                  <strong> edit</strong> or <strong>view-only</strong> permission.
                </p>
              </Section>

              <Section id="platform" icon={<BuildingOffice2Icon className={iconCls} />} title="Platform" tag="Super admins">
                <p className="m-0">
                  Platform-wide administration for super admins only: view every company on the
                  platform, see overall statistics, and manage the shared ISO reference data
                  (threats, vulnerabilities, controls and standard wording) that all companies draw
                  on. Most users never see this area.
                </p>
              </Section>

              <p className="text-xs px-1 pt-2" style={{ color: 'var(--color-text-subtle)' }}>
                Can't find what you need, or something isn't behaving as expected? Contact your
                company administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
